/** @format */

import { HttpClient } from '@angular/common/http';
import { Component, Inject, NgZone } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { VizFormModule } from '../../common/components/form/form.module';
import { ToolTipRendererDirective } from '../../common/components/tooltip/tooltip.directive';
import { ConfirmDialogComponent } from '../../common/dialogs/confirm.dialog';
import {
  CompareWithMixin,
  DELETED,
  EmptyObject,
  WebUtils,
  utils,
} from '../../common/utils';
import { AppValidators } from '../../common/validators';
import {
  AccessUtils,
  TLocation,
  TOrganization,
  TSpace,
  TSpaceFacility,
  TSpacePurpose,
  TSpaceType,
} from '../../core/entities';
import { SiteEditUtils } from '../../core/lang';
import { AppPermissions } from '../../core/permissions';
import { EDITOR_CONFIG } from '../../core/site.editor.utils';
import { thumbFile } from '../../core/upload.utils';
import { SessionCache } from '../../session.cache';
import { CONTENT_PATH, SessionService, TIMEZONE } from '../../session.service';
import { TScheduleCalendar } from '../../thinkdesks/thinkdesks';
import { ImageUploadBase } from '../../thinkdesks/widgets/image.upload.base';
import { ObjectImageListComponent } from '../../thinkdesks/widgets/object.image.list';
import { LImageUploadContainer } from '../../thinkdesks/widgets/upload.container';

@Component({
  templateUrl: './space.edit.dialog.html',
  standalone: true,
  imports: [
    VizFormModule,
    MatTabsModule,
    MatInputModule,
    MatSelectModule,
    MatDialogModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    CKEditorModule,
    ObjectImageListComponent,
    ToolTipRendererDirective,
    FontAwesomeModule,
    MatTooltipModule,
  ],
})
export class SpaceEditDialogComponent extends CompareWithMixin(EmptyObject) {
  permissions = AppPermissions;
  form: UntypedFormGroup;
  _common = _common;

  objectId: number;
  object: TSpace;

  api = '/api/space';

  organizations: TOrganization[] = [];
  locations: TLocation[] = [];
  schedules: TScheduleCalendar[] = [];
  spaceTypes: TSpaceType[] = [];
  spacePurposes: TSpacePurpose[] = [];
  spaceFacilities: TSpaceFacility[] = [];

  public Editor = ClassicEditor;
  config = EDITOR_CONFIG;

  container = new LImageUploadContainer();
  dlangs = SiteEditUtils.OTHER_LANGS;

  organization: TOrganization;
  location: TLocation;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    public session: SessionService,
    public http: HttpClient,
    public ngZone: NgZone,
    public snackBar: MatSnackBar,
    private cache: SessionCache,
    private router: Router,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<SpaceEditDialogComponent>
  ) {
    super();
    this.objectId = data.objectId;
    this.location = this.data.location;
    this.organization = this.data.organization;

    this.dialogRef.addPanelClass('full-dialog');

    this.cache.loadSpacePurposes((list) => {
      this.spacePurposes = list;
    });

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      name_lang: SiteEditUtils.langArray(this.fb),

      code: [''],
      active: [''],
      bookingMode: [''],
      autoApprove: [''],
      staffOnly: [''],
      maxBookings: [1, [Validators.min(1)]],
      enableMaxBookings: [false],

      approved: [''],
      locked: [''],
      description: [''],
      description_lang: SiteEditUtils.langArray(this.fb),

      textDescription: [''],
      textDescription_lang: SiteEditUtils.langArray(this.fb),

      organization: ['', Validators.required],
      location: ['', Validators.required],

      thinkdesksSpaceCode: [''],

      bookSchedule: ['', Validators.required],
      bookDays: [14, [Validators.required, AppValidators.above(0)]],
      bookMinSlots: ['', Validators.required],
      bookMaxSlots: ['', Validators.required],
      bookSlotIncrement: ['', Validators.required],
      facilities: this.fb.array([]),
      includedFacilities: [''],
      includedFacilities_lang: SiteEditUtils.langArray(this.fb),

      types: this.fb.array([]),
      space: [''],
      capacity: ['', [Validators.min(0)]],
      area: [''],
      remarks: [''],
      remarks_lang: SiteEditUtils.langArray(this.fb),
      purpose: ['', Validators.required],

      terms: [''],
      terms_lang: SiteEditUtils.langArray(this.fb),
    }, {
      validators: this.maxBookingsValidator()
    });
    this.form.controls['thinkdesksSpaceCode'].disable();
  }

  get formFacilities() {
    return this.form.controls['facilities'] as FormArray;
  }
  get formTypes() {
    return this.form.controls['types'] as FormArray;
  }

  updateLocation2(location: TLocation, cb?: () => void) {
    let ff = this.formFacilities;
    ff.clear();
    this.schedules = [];

    if (!location) return;
    this.cache.loadScheduleCalendar(location.id, (list) => {
      this.schedules = list;
    });
    this.cache.loadSpaceFacilities(location.id, (list) => {
      this.spaceFacilities = list;

      for (let t of this.spaceFacilities) {
        ff.push(new FormControl(false));
      }
      if (cb) cb();
    });
  }

  ////maxBookings validation: when enabled, must be at least 2 and not exceed capacity (if capacity > 0)
  private maxBookingsValidator(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const capacity = Number(form.get('capacity')?.value);
      const enableMaxBookings = !!form.get('enableMaxBookings')?.value;
      const maxBookings = Number(form.get('maxBookings')?.value);

      // Only validate when the toggle is ON
      if (!enableMaxBookings) {
        return null;
      }

      // Skip if maxBookings is empty, null or 0
      if (!maxBookings || maxBookings <= 0) {
        return null;
      }

      // Main rule: when capacity > 0, maxBookings must be <= capacity
      if (capacity > 0 && maxBookings > capacity) {
        return { maxBookingsExceedsCapacity: true };
      }

      return null;
    };
  }

  ngOnInit(): void {
    this.cache.loadOrganization(this.session, (list) => {
      let locations = this.session.locations.map((c) => c.organization?.id);
      this.organizations = list.filter((c) => locations.includes(c.id));

      this.cache.loadSpaceTypes((list) => {
        this.spaceTypes = list;
        let ff = this.formTypes;
        ff.clear();
        for (let t of this.spaceTypes) {
          ff.push(new FormControl(false));
        }

        if (this.objectId) {
          WebUtils.web_result(
            this.http.get(`${this.api}/${this.objectId}`),
            (result) => {
              this.object = result as TSpace;

              AccessUtils.fixSpace(this.object);

              let images = this.object.images ?? [];
              this.container.load(
                {
                  numImages: images.length,
                  imageIds: images.map((c) => null),
                  names: images,
                },
                (index, imageId) => {
                  return `${CONTENT_PATH}/space/${this.object.id}/${thumbFile(
                    images[index]
                  )}`;
                }
              );

              let query = Object.assign({}, this.object) as any;

              query.organization = query.location?.organization;

              utils.fill_form_values(query, this.form);

              //maxBookings initialization
              const currentMax = this.form.get('maxBookings')?.value;
              if (currentMax != null && Number(currentMax) >= 2) {
                this.form.patchValue({ enableMaxBookings: true }, { emitEvent: false });
              } else {
                this.form.patchValue({ enableMaxBookings: false }, { emitEvent: false });
                // Optional: force 1 when disabled
                this.form.get('maxBookings')?.setValue(1, { emitEvent: false });
              }

              this.updateForm();

              this.updateLocation();

              this.updateLocation2(query.location, () => {
                this.spaceFacilities.forEach((c, index) => {
                  let cc = this.formFacilities.controls[index];
                  cc.setValue(
                    !!this.object.facilities.find((p) => p.id == c.id)
                  );
                });
              });
              this.spaceTypes.forEach((c, index) => {
                let cc = this.formTypes.controls[index];
                cc.setValue(!!this.object.types.find((p) => p.id == c.id));
              });
              this.updateKey();
            }
          );
        } else {
          let locations = this.session.locations;

          if (locations.length == 1) {
            if (!this.organization) {
              this.location = locations[0];
              this.organization = this.location.organization;
            }
          }
          if (!this.organization && this.organizations.length == 1) {
            this.organization = this.organizations[0];
          }

          let query = {
            timezone: TIMEZONE,
            organization: this.organization,
            location: this.location,
            active: false,
            autoApprove: true,
            bookingMode: true,
            bookDays: 14,
            bookSlotMins: 1,
            bookMinSlots: 1,
            bookMaxSlots: 16,
            bookSlotIncrement: 1,
            maxBookings: 1,
          } as any;

          utils.fill_form_values(query, this.form);

          this.updateForm();

          this.updateLocation();
        }
      });
    });

    //Newly added for enableMaxBookings
    this.form.get('enableMaxBookings')?.valueChanges.subscribe(enabled => {
      const maxCtrl = this.form.get('maxBookings');

      if (!enabled) {
        // Turning OFF → force reset to 1 + clear any errors
        maxCtrl?.setValue(1, { emitEvent: false, emitModelToViewChange: true });
        maxCtrl?.markAsUntouched();
        maxCtrl?.markAsPristine();
        maxCtrl?.clearValidators();     // remove ALL validators (safer)
        maxCtrl?.updateValueAndValidity({ emitEvent: false });
      } else {
        // Turning ON → enforce minimum 2
        const current = maxCtrl?.value;
        if (!current || current < 2) {
          maxCtrl?.setValue(2, { emitEvent: false });
        }

        // Re-apply min validator
        maxCtrl?.setValidators([Validators.min(2)]);   // or addValidators if you have others
        maxCtrl?.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  key = {};
  subkey = {};
  updateKey() {
    this.subkey = null;

    if (this.object.bookSchedule) {
      this.key = {
        calendar: WebUtils.objName(this.object.bookSchedule),
      };
      this.subkey = {
        zone: WebUtils.objName(this.object),
      };
    } else {
      this.key = {
        zone: WebUtils.objName(this.object),
      };
    }
  }
  updateLocation() {
    let location =
      (this.object?.location as TLocation) ?? this.form.value.location;
    let organization = this.form.value.organization ?? location?.organization;
    this.locations = [];
    this.schedules = [];
    if (organization) {
      this.cache.loadLocation(
        organization.id,
        (list) => {
          this.locations = list;
        },
        this.session.locations
      );
    }
    if (location) {
      this.updateLocation2(location as TLocation);
    }
  }

  updateForm() {
    if (!this.session.hasPermission(AppPermissions.SPACE_EDIT)) {
      this.form.disable();
    } else {
      this.form.enable();
    }
    if (this.objectId) {
      this.form.controls['organization'].disable();
      this.form.controls['location'].disable();
    }
    utils.updateValidity(this.form);
  }
  save() {
    if (!this.form.valid)
      return utils.validateAllFormFields(this.form, {
        snackBar: this.snackBar,
      });

    let formData = new FormData();

    let query = Object.assign({}, this.form.value);
    utils.form_clean_numbers(query, ['capacity', 'area']);
    query.images = ImageUploadBase.getFile(formData, 'images', this.container);
    SiteEditUtils.langValues(query, 'name');
    SiteEditUtils.langValues(query, 'description');
    SiteEditUtils.langValues(query, 'remarks');
    SiteEditUtils.langValues(query, 'textDescription');
    SiteEditUtils.langValues(query, 'includedFacilities');
    SiteEditUtils.langValues(query, 'terms');

    if (!this.objectId) {
      query.createdMember = WebUtils.obj(this.session.user);
    }
    query.type = WebUtils.objId(query.type);
    query.bookSchedule = WebUtils.objName(query.bookSchedule);

    query.facilities = this.spaceFacilities
      .filter((c, index) => query.facilities[index])
      .map((c) => WebUtils.objName(c));
    query.types = this.spaceTypes
      .filter((c, index) => query.types[index])
      .map((c) => WebUtils.objName(c));

    utils.form_clean_numbers(query, ['lat', 'lng']);
    formData.append('data', JSON.stringify(query));

    if (this.objectId) {
      WebUtils.web_result(
        this.http.put(`${this.api}/${this.objectId}`, formData),
        async (result) => {
          this.object = result as TSpace;
          AccessUtils.fixSpace(this.object);

          this.dialogRef.close(this.object);
        }
      );
    } else {
      WebUtils.web_result(
        this.http.post(`${this.api}`, formData),
        async (result) => {
          this.object = result as TSpace;
          AccessUtils.fixSpace(this.object);

          this.dialogRef.close(this.object);
        }
      );
    }
  }

  cancel() {
    this.dialogRef.close();
  }
  closeDialog() {
    this.dialogRef.close();
  }
  remove() {
    ConfirmDialogComponent.open(
      this.dialog,
      _common.delete,
      _common.delete_confirm,
      () => {
        WebUtils.web_result(
          this.http.delete(`${this.api}/${this.objectId}`),
          (result) => {
            this.dialogRef.close(DELETED);
          },
          this.snackBar
        );
      }
    );
  }
  bookingPage() {
    this.session.lang;
    let url = `/${this.session.lang}/space/${this.objectId}/book`;
    //this.router.navigate([url]);
    //this.dialogRef.close();
    window.open(url, '_blank');
  }
}
